// @flow
import React, {
  useState,
  useEffect,
} from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  DateTime,
} from 'luxon';
import {
  getStatus as getResourceStatus,
} from 'redux-resource';

import type {
  Dispatch,
} from 'chronos-types';

import * as selectors from 'chronos-selectors';
import * as actions from 'chronos-actions';
import {
  validateJiraTime,
  jts,
} from 'chronos-utils';

import {
  FieldTextAreaStateless,
} from '@atlaskit/field-text-area';
import {
  FieldTextStateless,
} from '@atlaskit/field-text';
import {
  DateTimePicker,
} from '@atlaskit/datetime-picker';
import Button, {
  ButtonGroup,
} from '@atlaskit/button';
import {
  Checkbox,
} from '@atlaskit/checkbox';
import Spinner from '@atlaskit/spinner';
import ModalDialog, {
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';

import {
  Flex,
} from 'chronos-styled';
import {
  SelectAsyncPaginate,
} from 'chronos-components';

import RemainingEstimatePicker from './RemainingEstimatePicker';
import {
  InputLabel,
  InputExample,
  ModalContentContainer,
} from './styled';


const mapState = state => ({
  isOpen: selectors.getUiState('worklogFormModalIsOpen')(state),
  worklogFormDate: selectors.getUiState('worklogFormDate')(state),
  worklog: selectors.getEditWorklog(state),
  defaultOptions: selectors.getAllIssuesAsOptions(state),
  saveInProcess: getResourceStatus(
    state,
    'worklogs.requests.saveWorklog.status',
  ).pending,
  worklogIssue: (
    selectors.getResourceItemBydId(
      'issues',
      selectors.getUiState('worklogFormIssueId')(state),
    )(state)
  ),
});

let timeInput = null;

const WorklogModal: React.StatelessFunctionalComponent<{}> = (): React.Node => {
  const {
    isOpen,
    worklog,
    worklogIssue,
    worklogFormDate,
    defaultOptions,
    saveInProcess,
  } = useSelector(mapState);
  const dispatch: Dispatch = useDispatch();

  const [startTime, setStartTime] = useState(
    worklogFormDate
      ? (
        `${worklogFormDate.toISODate()}T${DateTime.local().toISOTime()}`
      ) : (
        `${DateTime.local().toISODate()}T${DateTime.local().toISOTime()}`
      ),
  );
  const [issue, setIssue] = useState(
    worklog?.issue
    || worklogIssue,
  );
  const [timeSpent, setTimeSpent] = useState('20m');

  const [remainingEstimateValue, setRemainigEstimateValue] = useState('auto');
  const [remainingEstimateSetTo, setRemainigEstimateSetTo] = useState('');
  const [remainingEstimateReduceBy, setRemainigEstimateReduceBy] = useState('');

  const [adjustStartTime, setAdjustStartTime] = useState(true);
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState({
    comment: '',
    timeSpent: '',
  });

  useEffect(
    () => {
      if (isOpen) {
        if (worklog) {
          setIssue(worklog.issue);
          setTimeSpent(worklog.timeSpent);
          const worklogStarted = DateTime.fromISO(worklog.started);
          setStartTime(
            `${worklogStarted.toISODate()}T${worklogStarted.toISOTime()}`,
          );
          setComment(worklog.commentText);
        } else {
          setIssue(
            worklogIssue
            || null,
          );
          setTimeSpent('');
          const now = DateTime.local();
          if (adjustStartTime) {
            const newStartTime = now.minus(
              jts(timeSpent) * 1000,
            );
            setStartTime(
              `${worklogFormDate.toISODate()}T${newStartTime.toISOTime()}`,
            );
          } else {
            setStartTime(
              `${worklogFormDate.toISODate()}T${now.toISOTime()}`,
            );
          }
        }
        setTimeout(() => {
          if (timeInput) {
            timeInput.focus();
          }
        }, 50);
      } else {
        setComment('');
        setErrors({
          comment: '',
          timeSpent: '',
        });
      }
    },
    [isOpen],
  );

  const validate = () => {
    let valid = true;

    const condition = (
      (timeSpent === '')
      || !validateJiraTime(timeSpent)
    );

    if (condition) {
      errors.timeSpent = 'Invalid time format';
      valid = false;
    } else {
      errors.timeSpent = '';
    }

    setErrors(errors);

    return valid;
  };

  return isOpen && (
    <ModalDialog
      onClose={() => {
        dispatch(actions.setUiState({
          worklogFormModalIsOpen: false,
          editWorklogId: null,
        }));
      }}
      footer={() => (
        <ModalFooter>
          <Flex
            row
            style={{
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <ButtonGroup>
              <Button
                appearance="primary"
                disabled={saveInProcess}
                onClick={() => {
                  const isValid = validate();
                  if (isValid) {
                    dispatch(actions.saveWorklogRequest({
                      worklogId: (
                        worklog ? (
                          worklog.id
                        ) : (
                          null
                        )
                      ),
                      issueId: issue.id,
                      startTime,
                      adjustEstimate: remainingEstimateValue,
                      newEstimate: remainingEstimateSetTo,
                      reduceBy: remainingEstimateReduceBy,
                      timeSpent,
                      comment,
                    }));
                    setComment(comment);
                    setErrors({
                      comment: '',
                      timeSpent: '',
                    });
                  }
                }}
                iconAfter={(
                  saveInProcess
                    ? (
                      <Spinner invertColor />
                    ) : (
                      null
                    )
                )}
              >
                Log work
              </Button>
              <Button
                appearance="subtle"
                onClick={() => {
                  dispatch(actions.setUiState({
                    worklogFormModalIsOpen: false,
                    editWorklogId: null,
                  }));
                }}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </Flex>
        </ModalFooter>
      )}
      header={() => (
        <ModalHeader>
          <ModalTitle>
            {`${worklog ? 'Edit' : 'Add'} worklog`}
          </ModalTitle>
        </ModalHeader>
      )}
    >
      <ModalContentContainer style={{ minHeight: 360 }}>
        <Flex
          row
          align="flex-end"
          width="100%"
        >
          <div style={{ width: '80%' }}>
            <SelectAsyncPaginate
              name="issue"
              placeholder="Select issue"
              classNamePrefix="chronosPopupSelect"
              cacheUniq="issues"
              cacheOptions="issues"
              selectedOptions={[]}
              value={(
                issue
                  ? ({
                    label: `${issue.key} - ${issue.fields.summary}`,
                    value: issue.id,
                  }) : null
              )}
              loadOptions={(query, options) => (
                new Promise(
                  resolve => (
                    query.length
                      ? (
                        dispatch(actions.loadSelectIssuesOptionsRequest({
                          query,
                          loadedQty: options.length,
                          resolve,
                        }))
                      ) : (
                        resolve({
                          options: defaultOptions,
                          hasMore: false,
                        })
                      )
                  ),
                )
              )}
              onChange={(option) => {
                setIssue(option.issue);
              }}
            />
          </div>
        </Flex>
        {/* TIME SPENT */}
        <Flex
          row
          align="flex-end"
          width="100%"
        >
          <Flex>
            <FieldTextStateless
              label="Time spent"
              value={timeSpent}
              onChange={(ev) => {
                const newTimeSpent = ev.target.value || '';
                const newTimeSpentInSeconds = jts(newTimeSpent);
                if (!Number.isNaN(newTimeSpentInSeconds)) {
                  setTimeSpent(newTimeSpent);
                  if (adjustStartTime) {
                    const newStartTime = DateTime.fromISO(startTime).minus(
                      jts(newTimeSpent) * 1000,
                    );
                    setStartTime(`${newStartTime.toISODate()}T${newStartTime.toISOTime()}`);
                  }
                }
              }}
              required
              isInvalid={errors.timeSpent !== ''}
              invalidMessage={errors.timeSpent}
              innerRef={(ref) => {
                timeInput = ref; // eslint-disable-line
              }}
            />
          </Flex>
          <InputExample>
            (eg. 3w 4d 12h 30m)
          </InputExample>
        </Flex>

        {/* DATE */}
        <Flex
          column
          width="100%"
        >
          <InputLabel>
            Date time
          </InputLabel>
          <Flex
            spacing={8}
            width="100%"
            align="center"
          >
            <DateTimePicker
              value={startTime}
              innerProps={{
                style: {
                  width: '40%',
                },
              }}
              onChange={(newDateTime) => {
                setStartTime(newDateTime);
              }}
            />
            <Checkbox
              name="adjust_start_time"
              id="adjust_start_time"
              isChecked={adjustStartTime}
              label="Adjust start time according to time spend"
              onChange={() => {
                setAdjustStartTime(!adjustStartTime);
              }}
            />
          </Flex>
        </Flex>

        {/* ESTIMATE */}
        <RemainingEstimatePicker
          issue={issue}
          value={remainingEstimateValue}
          onChange={(value) => {
            setRemainigEstimateValue(value);
          }}
          onReduceByChange={(value) => {
            setRemainigEstimateReduceBy(value);
          }}
          onNewChange={(value) => {
            setRemainigEstimateSetTo(value);
          }}
          newValue={remainingEstimateSetTo}
          showReduceBy={worklog === null}
          reduceByValue={remainingEstimateReduceBy}
        />

        {/* COMMENT */}
        <FieldTextAreaStateless
          shouldFitContainer
          label="Worklog comment"
          isInvalid={errors.comment !== ''}
          invalidMessage={errors.comment}
          value={comment || ''}
          onChange={(ev) => {
            setComment(ev.target.value);
          }}
        />
      </ModalContentContainer>
    </ModalDialog>
  );
};

export default WorklogModal;
